import { useState } from "react";
import cx from "classnames";
import { useTranslation } from "react-i18next";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faCircleCheck } from "@fortawesome/pro-regular-svg-icons";
import { Badge, Label } from "../../common";
import Toast from "../../common/Toast";
import { ToastConfig } from "../../common/ToastConfig";
import Sidebar from "../../common/Sidebar";
import PeoplePicker from "../../common/PeoplePicker";
import UserSearchDto from "../../../types/dtos/generic/UserSearchDto";
import feedbackRequestApi from "../../../api/feedback/feedbackRequestApi";
import {
  FeedbackMode,
  FeedbackRequestAccessMode,
} from "../../../types/feedback/FeedbackRequestDto";
import { normalizeFeedbackMode } from "../../../helpers/feedbackModeHelper";
import ResultStatus from "../../../types/dtos/generic/ResultStatus";

interface RequestFeedbackSidebarProps {
  isOpen: boolean;
  onOpenChange: (open: boolean) => void;
  answerSetUniqueId: string;
  feedbackMode: FeedbackMode;
  participantUserIds: number[];
  subjectUserId: number | null;
  loggedInUserId: number;
  api: feedbackRequestApi;
  onRequestCreated: () => void;
  defaultRecipientUserId: number | null;
  defaultRecipientName: string | null;
}

interface AccessModeOption {
  mode: FeedbackRequestAccessMode;
  title: string;
  description: string;
}

function RequestFeedbackSidebar({
  isOpen,
  onOpenChange,
  answerSetUniqueId,
  feedbackMode,
  participantUserIds,
  subjectUserId,
  loggedInUserId,
  api,
  onRequestCreated,
  defaultRecipientUserId,
  defaultRecipientName,
}: RequestFeedbackSidebarProps) {
  const { t } = useTranslation();
  const [selectedUserId, setSelectedUserId] = useState<number | null>(
    defaultRecipientUserId
  );
  const [message, setMessage] = useState<string>("");
  const [requestedAccessMode, setRequestedAccessMode] =
    useState<FeedbackRequestAccessMode>("FEEDBACKONLY");
  const [isSaving, setIsSaving] = useState<boolean>(false);
  const [toastConfig, setToastConfig] = useState<ToastConfig>(
    ToastConfig.hidden()
  );
  const [pickerKey, setPickerKey] = useState<number>(0);

  const resolvedFeedbackMode = normalizeFeedbackMode(feedbackMode);

  const isSubjectEmployeeChoice =
    resolvedFeedbackMode === "SUBJECTEMPLOYEECHOICE";
  const isSubjectEmployee =
    subjectUserId !== null && subjectUserId === loggedInUserId;

  const effectiveRequestAccessMode = isSubjectEmployeeChoice
    ? isSubjectEmployee
      ? requestedAccessMode
      : "FEEDBACKONLY"
    : resolvedFeedbackMode;

  const messageIsRequired =
    effectiveRequestAccessMode === "FEEDBACKONLY";
  const canSubmit =
    selectedUserId !== null &&
    (!messageIsRequired || message.trim().length > 0) &&
    !isSaving;

  const accessModeOptions: AccessModeOption[] = [
    {
      mode: "FEEDBACKONLY",
      title: t("Feedback.Request.AccessMode.FeedbackOnlyTitle", {
        defaultValue: "Feedback only",
      }),
      description: t("Feedback.Request.AccessMode.FeedbackOnlyDescription", {
        defaultValue:
          "They submit feedback from their dashboard and cannot view this document. Include enough context in your message.",
      }),
    },
    {
      mode: "FULLREVIEWACCESS",
      title: t("Feedback.Request.AccessMode.FullReviewTitle", {
        defaultValue: "Review + Feedback",
      }),
      description: t("Feedback.Request.AccessMode.FullReviewDescription", {
        defaultValue:
          "They can view this document and add their own feedback notes. They can't change any answers.",
      }),
    },
  ];

  const resetForm = () => {
    setSelectedUserId(defaultRecipientUserId);
    setMessage("");
    setRequestedAccessMode("FEEDBACKONLY");
    setIsSaving(false);
    setPickerKey((prev) => prev + 1);
  };

  const handleClose = (open: boolean) => {
    if (!open) {
      resetForm();
    }
    onOpenChange(open);
  };

  const handleSubmit = () => {
    if (!canSubmit) return;

    setIsSaving(true);

    api.createRequest(
      {
        recipientUserId: selectedUserId!,
        message: message.trim() || null,
        answerSetUniqueId,
        requestedAccessMode:
          isSubjectEmployeeChoice && isSubjectEmployee
            ? requestedAccessMode
            : null,
      },
      (_data: ResultStatus) => {
        setIsSaving(false);
        resetForm();
        onOpenChange(false);
        onRequestCreated();
        setToastConfig(
          ToastConfig.success("Request Sent", "Your feedback request has been sent.")
        );
      },
      (errorMessage: string) => {
        setIsSaving(false);
        setToastConfig(
          ToastConfig.error("Request Failed", errorMessage)
        );
      }
    );
  };

  const handlePeoplePickerChange = (
    value: number | UserSearchDto | UserSearchDto[] | null
  ) => {
    if (typeof value === "number") {
      setSelectedUserId(value);
    } else if (value === null) {
      setSelectedUserId(null);
    }
  };

  const modeDescription =
    resolvedFeedbackMode === "FEEDBACKONLY"
      ? t("Feedback.Request.ModeDescription.FeedbackOnly", {
          defaultValue:
            "The recipient will receive a notification and can submit their feedback from their dashboard. They will not have access to the review document, so please include enough context in your message for them to provide meaningful feedback.",
        })
      : resolvedFeedbackMode === "FULLREVIEWACCESS"
        ? t("Feedback.Request.ModeDescription.FullReview", {
            defaultValue:
              "The recipient will be able to view this document in read-only mode (Review + Feedback) and leave their feedback notes at the bottom.",
          })
        : isSubjectEmployeeChoice && isSubjectEmployee
          ? t("Feedback.Request.ModeDescription.SubjectEmployeeChoice", {
              defaultValue:
                "Choose whether the recipient can view this document or only submit feedback from their dashboard.",
            })
          : null;

  const accessTilesDisabled = isSubjectEmployeeChoice && !isSubjectEmployee;

  return (
    <Sidebar
      isOpen={isOpen}
      onOpenChange={handleClose}
      title="Request Feedback"
      onPrimaryButtonClick={handleSubmit}
      primaryButtonText={isSaving ? "Sending..." : "Send Request"}
      onSecondaryButtonClick={() => handleClose(false)}
      secondaryButtonText="Cancel"
      isPrimaryButtonDisabled={!canSubmit}
      isLoading={isSaving}
      width="NARROW"
      preventInitialAutoFocus
    >
      <div className="space-y-5 p-2">
        {modeDescription && (
          <p className="text-sm text-gray-500">{modeDescription}</p>
        )}

        {isSubjectEmployeeChoice && (
          <div>
            <Label
              htmlFor="feedback-access-mode"
              text={t("Feedback.Request.AccessLabel", {
                defaultValue: "Recipient access",
              })}
            />
            <div
              id="feedback-access-mode"
              className="mt-2 space-y-2"
              role="radiogroup"
              aria-label={t("Feedback.Request.AccessLabel", {
                defaultValue: "Recipient access",
              })}
            >
              {accessModeOptions.map((option) => {
                const isSelected = requestedAccessMode === option.mode;

                return (
                  <button
                    key={option.mode}
                    type="button"
                    role="radio"
                    aria-checked={isSelected}
                    disabled={accessTilesDisabled}
                    onClick={() => setRequestedAccessMode(option.mode)}
                    className={cx(
                      "w-full text-left p-3 border rounded-lg transition-all duration-200",
                      accessTilesDisabled
                        ? cx(
                            "cursor-not-allowed",
                            isSelected
                              ? "border-primary bg-white ring-1 ring-primary ring-opacity-30"
                              : "opacity-60 border-gray-200 bg-gray-50"
                          )
                        : "cursor-pointer",
                      !accessTilesDisabled &&
                        (isSelected
                          ? "border-primary bg-white shadow-md ring-1 ring-primary ring-opacity-30"
                          : "border-gray-300 bg-white shadow-sm hover:border-primary hover:shadow-md hover:ring-1 hover:ring-primary-pale")
                    )}
                  >
                    <div className="flex items-start w-full gap-2">
                      <div className="flex-grow min-w-0">
                        <span
                          className={cx(
                            "block text-sm font-medium",
                            isSelected ? "text-primary" : "text-gray-800"
                          )}
                        >
                          {option.title}
                        </span>
                        <span className="block text-xs text-gray-500 mt-1 leading-relaxed">
                          {option.description}
                        </span>
                      </div>
                      <div
                        className={cx(
                          "min-w-[28px] flex items-center justify-end shrink-0 pt-0.5",
                          isSelected ? "text-primary" : "text-transparent"
                        )}
                        aria-hidden={!isSelected}
                      >
                        <FontAwesomeIcon
                          icon={faCircleCheck}
                          className="h-5 w-5"
                        />
                      </div>
                    </div>
                  </button>
                );
              })}
            </div>
            {accessTilesDisabled && (
              <p className="mt-2 text-xs text-gray-500 leading-relaxed">
                {t("Feedback.Request.ManagerAccessNote", {
                  defaultValue:
                    "Only the employee can choose to give someone access to this document.",
                })}
              </p>
            )}
          </div>
        )}

        <div>
          <Label htmlFor="feedback-recipient" text="Recipient" />
          <PeoplePicker
            key={pickerKey}
            inputId="feedback-recipient"
            onPeoplePickerChange={handlePeoplePickerChange}
            excludeLoggedInUserFromResults
            nonMultiSelectChangeValueType="NUMERIC-ID"
            placeholder="Search for a person..."
            lockedMemberIds={participantUserIds}
            lockedMemberTooltip="This person is already a participant in this document"
            allowClearField
            onClearFieldClick={() => setSelectedUserId(null)}
            initialInput={defaultRecipientName ?? undefined}
          />
        </div>

        <div>
          <div className="flex items-center">
            <Label htmlFor="feedback-message" text="Message" />
            {!messageIsRequired && (
              <Badge
                text={t("Common.Optional")}
                backgroundColourClassName="bg-gray-200/75"
                textColourClassName="text-gray-400"
                marginClassName="ml-1"
              />
            )}
          </div>
          <textarea
            id="feedback-message"
            value={message}
            onChange={(e) => setMessage(e.target.value)}
            placeholder="Add a message to the recipient..."
            className="w-full px-3 py-2 text-sm border border-gray-300 rounded-md bg-white resize-none focus:outline-none focus:ring-1 focus:ring-primary focus:border-primary"
            rows={4}
            maxLength={1000}
          />
        </div>
      </div>
      {toastConfig.show && (
        <Toast
          title={toastConfig.title}
          message={toastConfig.message}
          type={toastConfig.type}
          onClose={() => setToastConfig(ToastConfig.hidden())}
        />
      )}
    </Sidebar>
  );
}

export default RequestFeedbackSidebar;
